import api from './config';
import { USER_ME_URI } from '@/constants';
import type { LanguageType, UserInfoType } from '@/types';
import { normalizeServiceId } from '@/constants/streamingServices';
import { handleApiError } from '@/utils/apiErrorHandler';
import { getTokens } from '@/stores/secure';
import { useAuthStore } from '@/stores/AuthState';

const extractServiceName = (
  svc: string | { name?: string } | null | undefined,
): UserInfoType['favorite_streaming_service'] => {
  const name = typeof svc === 'string' ? svc : svc?.name;
  if (!name) return null;
  return normalizeServiceId(name) as UserInfoType['favorite_streaming_service'];
};

/**
 * Current authenticated user (`/users/me`). Populates and maps the favorite
 * streaming service so the store reflects it (star / "Ajouter" CTA). Throws on
 * failure so React Query enters its error state (security fix R-025).
 */
export const fetchUserInfo = async (): Promise<UserInfoType> => {
  try {
    const response = await api.get(
      `${USER_ME_URI}?populate[language]=*&populate[favorite_external_service]=*`,
    );
    const data = (response.data?.data || response.data) as Record<string, unknown>;
    return {
      ...data,
      favorite_streaming_service: extractServiceName(
        data?.favorite_external_service as string | { name?: string } | null | undefined,
      ),
    } as UserInfoType;
  } catch (error) {
    return handleApiError(error, 'fetchUserInfo');
  }
};

/** Update the current user (e.g. username). Backend: PATCH /users/me. */
export const updateUser = async (data: { username?: string }): Promise<UserInfoType> => {
  try {
    const response = await api.patch(USER_ME_URI, data);
    return (response.data?.data || response.data) as UserInfoType;
  } catch (error) {
    return handleApiError(error, 'updateUser');
  }
};

/** Persist the user's language. Backend: PUT /languages/update-user. */
export const changeUserLanguage = async (language: LanguageType): Promise<void> => {
  try {
    await api.put('/languages/update-user', { language });
  } catch (error) {
    return handleApiError(error, 'changeUserLanguage');
  }
};

/**
 * profile-sync-on-launch — re-read the profile from the account and apply it.
 *
 * The persisted store only paints the first frame: nothing re-read `/users/me`
 * on launch, and the OAuth login payload carries no favourite service, so the
 * selected service vanished after a login and never came back without a manual
 * refresh. Fire-and-forget from the callers.
 */
export async function syncUserFromServer(): Promise<void> {
  // No session ⇒ no request: `/users/me` would answer 401, and the API
  // interceptor would try a refresh and could force a logout.
  const { bearerToken } = await getTokens();
  if (!bearerToken) return;
  let fresh: UserInfoType;
  try {
    fresh = await fetchUserInfo();
  } catch {
    // Offline or server down: the device copy stays, nothing else happens.
    return;
  }
  // `fetchUserInfo` always sets `favorite_streaming_service` (null when the
  // account has none), so the account wins over the device, emptiness included.
  await useAuthStore.getState().setUserInfo(fresh as never);
}
